/**
 * The original's framed board, generalised.
 *
 * `menu-chrome.ts` measured one of these — the Main Menu — pixel by pixel off
 * `docs/game/ui-reference/main-menu.png`. Two more of the same widget appear
 * in the captures this lane works from (`career-menu.png`, a narrow four-row
 * board; `fly-single-mission.png`, a wide two-column thirteen-row board), so
 * the numbers that were constants there are parameters here. Every rule below
 * was re-derived against all three captures rather than assumed from one:
 *
 * - banner body width = the FONT2 title's advance width **+ 12**, exact for
 *   `MAIN MENU` (66→78), `CAREER MENU` (76→88) and `FLY SINGLE MISSION`
 *   (109→121);
 * - banner centre = board centre + 3, and the banner is always 17 rows tall
 *   sitting directly on the board's top edge, with five rows of shoulder
 *   splaying one pixel per row per side;
 * - the ornaments are real `ESNTL.BMP` frames, template-matched at **zero**
 *   mismatched pixels for the medallion (frame 5) and the pendant (frame 7)
 *   and one for the banner brackets (frame 6), on all three captures, at
 *   `(cx-15, y-3)`, `(cx-9, y+h)` and `(bannerLeft-19 | bannerRight-4, top+1)`.
 *
 * What could *not* be reduced to a rule is the ridge/footer bar insets: they
 * are neither constant nor proportional across the two board widths, so they
 * stay in the spec as measured numbers.
 */

import {
  bannerField,
  bevelRings,
  type BevelStack,
  BOARD_FIELD,
  type DrawOp,
  fill,
  type FontId,
  type Rect,
  type Span,
  spanRun,
  type TextStyle,
} from "./draw-ops.ts";
import {
  ORANGE_245,
  ORANGE_247,
  ORANGE_251,
  ORANGE_DARKEST,
  ORANGE_HIGHLIGHT,
  ORANGE_LABEL,
  ORANGE_LIGHTEST,
  ORANGE_PLATE,
  TAN_226,
  TAN_232,
  TAN_233,
  TAN_234,
  TAN_236,
  TAN_FACE,
  TAN_LIGHTEST,
} from "./ps-palette.ts";
import { type FontAtlas, measureText } from "./text-metrics.ts";

export const ORNAMENT_SHEET = "sprites/ESNTL.BMP";

export const BOARD_RINGS: BevelStack = [
  [TAN_LIGHTEST, TAN_232, TAN_232],
  [TAN_FACE, TAN_FACE, TAN_FACE],
  [TAN_234, TAN_LIGHTEST, TAN_LIGHTEST],
];

export const BUTTON_HEIGHT = 18;
export const BUTTON_PITCH = 20;
/** Buttons sit 11px in from each side of the board — the same on all three captured boards. */
export const BOARD_SIDE_INSET = 11;
/** The first button row starts 20px below the board's top edge. */
export const BOARD_FIRST_ROW = 20;
export const BUTTON_LABEL_INSET_Y = 2;
/**
 * How far inside the button's rect its orange plate starts, on every side —
 * the five bevel rings below. Measured, not assumed: on all nine action-row
 * buttons of `mission-assignment.png`, `patrol-briefing.png` and
 * `flight-assignments.png` the plate runs `rect.x + 5 .. rect.x + w - 6`,
 * without exception.
 *
 * Exported because it is the width a *label* has to fit, which is a fact the
 * layout side needs (`panel-parts.ts`'s `ACTION_PADDING_MIN`) and had been
 * quietly disagreeing with.
 */
export const BUTTON_PLATE_INSET = 5;
/** How far a pressed button's label shifts down and right — see `pressedRings`. */
export const BUTTON_PRESS_NUDGE = 1;
export const BUTTON_FONT: FontId = "FONT1";
export const BANNER_FONT: FontId = "FONT2";
export const BANNER_HEIGHT = 17;
/** Rows of shoulder before the banner's sides run straight. */
const BANNER_SPLAY = 5;

const BUTTON_RINGS: BevelStack = [
  [TAN_LIGHTEST, TAN_236, TAN_234],
  [TAN_226, TAN_233, TAN_232],
  [TAN_FACE, TAN_FACE, TAN_FACE],
  [ORANGE_LIGHTEST, ORANGE_245, ORANGE_245],
  [ORANGE_251, ORANGE_247, ORANGE_247],
];

export const BUTTON_LABEL_STYLE: TextStyle = {
  body: ORANGE_LABEL,
  shadow: ORANGE_DARKEST,
  highlight: ORANGE_HIGHLIGHT,
};

/**
 * A button the port has no screen behind. The original never shows one, so
 * this is the era's own idiom rather than a measurement: the orange plate and
 * its two lit rings drop to the board's tan, and the label is engraved in the
 * same tan instead of rust. It reads as part of the furniture — present,
 * named, and plainly not pressable — which is the point.
 */
const DISABLED_RINGS: BevelStack = [
  [TAN_LIGHTEST, TAN_236, TAN_234],
  [TAN_226, TAN_233, TAN_232],
  [TAN_FACE, TAN_FACE, TAN_FACE],
  [TAN_236, TAN_233, TAN_233],
  [TAN_234, TAN_236, TAN_236],
];

export const DISABLED_PLATE = TAN_234;
export const DISABLED_LABEL_STYLE: TextStyle = { body: TAN_236, shadow: 238, highlight: TAN_226 };

/**
 * The capture has no hover or focus state to copy — `PS.EXE` lights a button
 * only while the mouse is held down on it. Keyboard navigation still needs a
 * visible cursor, so the selected button is drawn *pressed*: the three outer
 * rings' top and bottom swap and the label shifts a pixel down-right.
 */
function pressedRings(stack: BevelStack): BevelStack {
  return stack.map((ring, depth) => (depth < 3 ? ([ring[1], ring[0], ring[2]] as const) : ring));
}

export interface BoardSpec {
  readonly frame: Rect;
  /** y of each button row, relative to `frame.y`. Usually `20 + 20*i`, but the wide grid has a gap before its last row. */
  readonly rows: readonly number[];
  readonly columns: number;
  readonly columnWidth: number;
  readonly columnGap: number;
  /** Side insets of the two ridge bars under the banner, and of the two footer bars below the board. */
  readonly ridgeInsets: readonly [number, number];
  readonly footerInsets: readonly [number, number];
}

/** Evenly spaced button rows: `20 + 20*i`, which is every captured board except the wide grid's last row. */
export function boardRows(count: number): number[] {
  return Array.from({ length: count }, (_, i) => BOARD_FIRST_ROW + i * BUTTON_PITCH);
}

/**
 * The frame a column layout implies: slots plus an 11px margin each side, and
 * `bottomPad` below the last row. Both captured single-column boards leave 7
 * (`MAIN MENU` 165px for 7 rows, `CAREER MENU` 105 for 4); the wide grid
 * leaves 6, so it passes its own.
 */
export function boardFrame(
  x: number,
  y: number,
  layout: Pick<BoardSpec, "rows" | "columns" | "columnWidth" | "columnGap">,
  bottomPad = 7,
): Rect {
  const inner = layout.columns * layout.columnWidth + (layout.columns - 1) * layout.columnGap;
  const last = layout.rows[layout.rows.length - 1] ?? BOARD_FIRST_ROW;
  return { x, y, w: inner + 2 * BOARD_SIDE_INSET, h: last + BUTTON_HEIGHT + bottomPad };
}

export function boardSlotRect(spec: BoardSpec, index: number): Rect {
  const column = index % spec.columns;
  const row = Math.floor(index / spec.columns);
  return {
    x: spec.frame.x + BOARD_SIDE_INSET + column * (spec.columnWidth + spec.columnGap),
    y: spec.frame.y + (spec.rows[row] ?? BOARD_FIRST_ROW + row * BUTTON_PITCH),
    w: spec.columnWidth,
    h: BUTTON_HEIGHT,
  };
}

export function boardFieldSpansOf(frame: Rect): Span[] {
  const spans: Span[] = [];
  for (let y = frame.y + 3; y < frame.y + frame.h - 3; y += 1) {
    spans.push({ x: frame.x + 3, y, w: frame.w - 6 });
  }
  return spans;
}

/** The banner's body rectangle: title width + 12, centred on the board centre + 3, resting on the board's top edge. */
export function bannerRectOf(frame: Rect, title: string, font: FontAtlas): Rect {
  const w = measureText(font, title) + 12;
  return { x: frame.x + Math.floor(frame.w / 2) + 3 - Math.floor(w / 2), y: frame.y - BANNER_HEIGHT, w, h: BANNER_HEIGHT };
}

/** `[y, left, right]` per row, shoulders splaying one pixel per side over the first five. */
export function bannerRows(banner: Rect): readonly (readonly [number, number, number])[] {
  const right = banner.x + banner.w - 1;
  return Array.from({ length: banner.h }, (_, i) => {
    const shrink = Math.max(0, BANNER_SPLAY - i);
    return [banner.y + i, banner.x + shrink, right - shrink] as const;
  });
}

export function bannerFieldSpansOf(banner: Rect): Span[] {
  return bannerRows(banner).map(([y, left, right]) => ({ x: left, y, w: right - left + 1 }));
}

/**
 * The banner's own bevel. Its lit top face runs light-to-dark inward
 * (`224,228,233`) while its shadowed sides run the other way, following the
 * shoulders round the corner where the splay ends.
 */
function bannerEdgeOps(banner: Rect): DrawOp[] {
  const ops: DrawOp[] = [];
  for (const [y, left, right] of bannerRows(banner)) {
    const row = y - banner.y;
    if (row < BANNER_SPLAY) {
      ops.push(fill({ x: left, y, w: 1, h: 1 }, TAN_LIGHTEST), fill({ x: right, y, w: 1, h: 1 }, TAN_LIGHTEST));
      if (row >= 1) ops.push(fill({ x: left + 1, y, w: 1, h: 1 }, TAN_FACE), fill({ x: right - 1, y, w: 1, h: 1 }, TAN_FACE));
      if (row >= 3) ops.push(fill({ x: left + 2, y, w: 2, h: 1 }, TAN_233), fill({ x: right - 3, y, w: 2, h: 1 }, TAN_233));
      continue;
    }
    ops.push(
      fill({ x: left, y, w: 1, h: 1 }, TAN_233),
      fill({ x: left + 1, y, w: 1, h: 1 }, TAN_FACE),
      fill({ x: left + 2, y, w: 1, h: 1 }, TAN_LIGHTEST),
      fill({ x: right, y, w: 1, h: 1 }, TAN_233),
      fill({ x: right - 1, y, w: 1, h: 1 }, TAN_FACE),
      fill({ x: right - 2, y, w: 1, h: 1 }, TAN_LIGHTEST),
    );
  }
  const capWidth = banner.w - 2 * BANNER_SPLAY;
  const capX = banner.x + BANNER_SPLAY;
  ops.push(
    spanRun(capX, banner.y, capWidth, TAN_LIGHTEST),
    spanRun(capX, banner.y + 1, capWidth, TAN_FACE),
    spanRun(capX, banner.y + 2, capWidth, TAN_234),
  );
  return ops;
}

/** A ridge bar: the two mitred light bars spanning the board behind the medallion. */
function ridgeBarOps(y: number, left: number, right: number): DrawOp[] {
  const width = right - left + 1;
  const ops: DrawOp[] = [
    spanRun(left, y, width, TAN_LIGHTEST),
    spanRun(left, y + 1, width, TAN_FACE),
    spanRun(left - 1, y + 2, width + 2, TAN_232),
  ];
  for (const [x, sign] of [
    [left, -1],
    [right, 1],
  ] as const) {
    ops.push(
      fill({ x: x + sign, y, w: 1, h: 1 }, 225),
      fill({ x: x + 2 * sign, y, w: 1, h: 1 }, 225),
      fill({ x: x + sign, y: y + 1, w: 1, h: 1 }, 229),
      fill({ x: x + 2 * sign, y: y + 1, w: 1, h: 1 }, TAN_232),
    );
  }
  return ops;
}

/** A footer bar: the same light bar with three-pixel stepped ends, drawn below the board. */
function footerBarOps(y: number, left: number, right: number): DrawOp[] {
  const inner = right - left - 5;
  const ops: DrawOp[] = [
    spanRun(left + 3, y, inner, TAN_LIGHTEST),
    spanRun(left + 2, y + 1, inner + 2, TAN_FACE),
    spanRun(left + 3, y + 2, inner, TAN_232),
  ];
  for (const [dx, color] of [
    [0, TAN_233],
    [1, 230],
    [2, 227],
  ] as const) {
    ops.push(fill({ x: left + dx, y, w: 1, h: 1 }, color), fill({ x: right - dx, y, w: 1, h: 1 }, color));
  }
  ops.push(
    fill({ x: left + 1, y: y + 1, w: 1, h: 1 }, TAN_233),
    fill({ x: right - 1, y: y + 1, w: 1, h: 1 }, TAN_233),
    fill({ x: left + 2, y: y + 2, w: 1, h: 1 }, TAN_233),
    fill({ x: right - 2, y: y + 2, w: 1, h: 1 }, TAN_233),
  );
  return ops;
}

function sprite(frame: number, x: number, y: number, flipH = false): DrawOp {
  return { kind: "sprite", sheet: ORNAMENT_SHEET, frame, x, y, flipH };
}

/** Medallion, banner brackets and pendant, at the offsets template-matching found on all three captured boards. */
export function boardOrnamentOps(frame: Rect, banner: Rect): DrawOp[] {
  const centre = frame.x + Math.floor(frame.w / 2);
  return [
    sprite(6, banner.x - 19, banner.y + 1, true),
    sprite(6, banner.x + banner.w - 5, banner.y + 1),
    sprite(5, centre - 15, frame.y - 3),
    sprite(7, centre - 9, frame.y + frame.h),
  ];
}

export interface BoardButton {
  readonly label: string;
  readonly enabled: boolean;
}

/** The orange plate inside a button's rect — the ground its label has to land on. */
export function buttonPlateRect(rect: Rect): Rect {
  return {
    x: rect.x + BUTTON_PLATE_INSET,
    y: rect.y + BUTTON_PLATE_INSET,
    w: rect.w - 2 * BUTTON_PLATE_INSET,
    h: rect.h - 2 * BUTTON_PLATE_INSET,
  };
}

/**
 * Where a button's label ink starts and how wide it runs — the arithmetic
 * `boardButtonOps` draws with, exported so a layout assertion can measure the
 * label the renderer will actually paint rather than restate the formula and
 * drift from it. `pressed` is the keyboard cursor's one-pixel down-right shift.
 */
export function buttonLabelSpan(rect: Rect, label: string, font: FontAtlas, pressed = false): { x: number; w: number } {
  const nudge = pressed ? 1 : 0;
  const w = measureText(font, label);
  return { x: rect.x + Math.floor(rect.w / 2) + 1 - Math.ceil(w / 2) + nudge, w };
}

export function boardButtonOps(rect: Rect, button: BoardButton, pressed: boolean, font: FontAtlas): DrawOp[] {
  const stack = button.enabled ? BUTTON_RINGS : DISABLED_RINGS;
  const span = buttonLabelSpan(rect, button.label, font, pressed);
  return [
    ...bevelRings(rect, pressed ? pressedRings(stack) : stack),
    fill(buttonPlateRect(rect), button.enabled ? ORANGE_PLATE : DISABLED_PLATE),
    {
      kind: "text",
      font: BUTTON_FONT,
      text: button.label,
      x: span.x,
      y: rect.y + BUTTON_LABEL_INSET_Y + (pressed ? 1 : 0),
      style: button.enabled ? BUTTON_LABEL_STYLE : DISABLED_LABEL_STYLE,
    },
  ];
}

export interface BoardRender {
  readonly title: string;
  readonly buttons: readonly BoardButton[];
  readonly selected: number;
  readonly font: FontAtlas;
  readonly bannerFont: FontAtlas;
}

/** The board, its banner, its bars and its ornaments — everything but the buttons and the background behind them. */
export function boardChromeOps(spec: BoardSpec, title: string, bannerFont: FontAtlas): DrawOp[] {
  const { frame } = spec;
  const banner = bannerRectOf(frame, title, bannerFont);
  const ops: DrawOp[] = [
    { kind: "field", style: BOARD_FIELD, spans: boardFieldSpansOf(frame) },
    ...bevelRings(frame, BOARD_RINGS),
    { kind: "field", style: bannerField(banner.x), spans: bannerFieldSpansOf(banner) },
    ...bannerEdgeOps(banner),
  ];
  spec.ridgeInsets.forEach((inset, i) =>
    ops.push(...ridgeBarOps(frame.y + 5 + 5 * i, frame.x + inset, frame.x + frame.w - 1 - inset)),
  );
  spec.footerInsets.forEach((inset, i) =>
    ops.push(...footerBarOps(frame.y + frame.h + 3 * i, frame.x + inset, frame.x + frame.w - 1 - inset)),
  );
  ops.push(...boardOrnamentOps(frame, banner));
  ops.push({ kind: "text", font: BANNER_FONT, text: title, x: banner.x + 7, y: banner.y + 2, style: { body: 0 } });
  return ops;
}

export function boardOps(spec: BoardSpec, input: BoardRender): DrawOp[] {
  const ops = boardChromeOps(spec, input.title, input.bannerFont);
  input.buttons.forEach((button, index) =>
    ops.push(...boardButtonOps(boardSlotRect(spec, index), button, index === input.selected, input.font)),
  );
  return ops;
}
